import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FaQuestion, 
    FaChevronDown, 
    FaChevronUp, 
    FaSearch, 
    FaLightbulb, 
    FaCrown, 
    FaUser, 
    FaGlobe, 
    FaShieldAlt, 
    FaHeadset, 
    FaRocket,
    FaGraduationCap,
} from 'react-icons/fa';
import Header from '../components/header';
import Footers from '../components/footers';

const FAQ = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default
    const [activeCategory, setActiveCategory] = useState('all');

    // FAQ categories and items
    const faqData = [
        {
            category: 'general',
            icon: FaQuestion,
            title: t('faqPage.categories.general.title'),
            items: [
                {
                    question: t('faqPage.categories.general.items.whatIs.question'),
                    answer: t('faqPage.categories.general.items.whatIs.answer')
                },
                {
                    question: t('faqPage.categories.general.items.howItWorks.question'),
                    answer: t('faqPage.categories.general.items.howItWorks.answer')
                },
                {
                    question: t('faqPage.categories.general.items.courseTypes.question'),
                    answer: t('faqPage.categories.general.items.courseTypes.answer')
                },
                {
                    question: t('faqPage.categories.general.items.freeToUse.question'),
                    answer: t('faqPage.categories.general.items.freeToUse.answer')
                }
            ]
        },
        {
            category: 'features',
            icon: FaRocket,
            title: t('faqPage.categories.features.title'),
            items: [
                {
                    question: t('faqPage.categories.features.items.languages.question'),
                    answer: t('faqPage.categories.features.items.languages.answer')
                },
                {
                    question: t('faqPage.categories.features.items.customize.question'),
                    answer: t('faqPage.categories.features.items.customize.answer')
                },
                {
                    question: t('faqPage.categories.features.items.multimedia.question'),
                    answer: t('faqPage.categories.features.items.multimedia.answer')
                },
                {
                    question: t('faqPage.categories.features.items.share.question'),
                    answer: t('faqPage.categories.features.items.share.answer')
                },
                {
                    question: t('faqPage.categories.features.items.knowledgeGrounding.question'),
                    answer: t('faqPage.categories.features.items.knowledgeGrounding.answer')
                },
                {
                    question: t('faqPage.categories.features.items.presentations.question'),
                    answer: t('faqPage.categories.features.items.presentations.answer')
                },
                {
                    question: t('faqPage.categories.features.items.replaceTeachers.question'),
                    answer: t('faqPage.categories.features.items.replaceTeachers.answer')
                },
                {
                    question: t('faqPage.categories.features.items.beta.question'),
                    answer: t('faqPage.categories.features.items.beta.answer')
                },
            ]
        },
        {
            category: 'account',
            icon: FaUser,
            title: t('faqPage.categories.account.title'),
            items: [
                {
                    question: t('faqPage.categories.account.items.createAccount.question'),
                    answer: t('faqPage.categories.account.items.createAccount.answer')
                },
                {
                    question: t('faqPage.categories.account.items.planDifference.question'),
                    answer: t('faqPage.categories.account.items.planDifference.answer')
                },
                {
                    question: t('faqPage.categories.account.items.upgradeDowngrade.question'),
                    answer: t('faqPage.categories.account.items.upgradeDowngrade.answer')
                },
                {
                    question: t('faqPage.categories.account.items.dataSecurity.question'),
                    answer: t('faqPage.categories.account.items.dataSecurity.answer')
                }
            ]
        },
        {
            category: 'technical',
            icon: FaShieldAlt,
            title: t('faqPage.categories.technical.title'),
            items: [
                {
                    question: t('faqPage.categories.technical.items.devicesBrowsers.question'),
                    answer: t('faqPage.categories.technical.items.devicesBrowsers.answer')
                },
                {
                    question: t('faqPage.categories.technical.items.offlineUse.question'),
                    answer: t('faqPage.categories.technical.items.offlineUse.answer')
                },
                {
                    question: t('faqPage.categories.technical.items.generationSpeed.question'),
                    answer: t('faqPage.categories.technical.items.generationSpeed.answer')
                },
                {
                    question: t('faqPage.categories.technical.items.incorrectContent.question'),
                    answer: t('faqPage.categories.technical.items.incorrectContent.answer')
                }
            ]
        },
        {
            category: 'learning',
            icon: FaGraduationCap,
            title: t('faqPage.categories.learning.title'),
            items: [
                {
                    question: t('faqPage.categories.learning.items.whoCanUse.question'),
                    answer: t('faqPage.categories.learning.items.whoCanUse.answer')
                },
                {
                    question: t('faqPage.categories.learning.items.businessCourses.question'),
                    answer: t('faqPage.categories.learning.items.businessCourses.answer')
                },
                {
                    question: t('faqPage.categories.learning.items.trackProgress.question'),
                    answer: t('faqPage.categories.learning.items.trackProgress.answer')
                },
                {
                    question: t('faqPage.categories.learning.items.collaborate.question'),
                    answer: t('faqPage.categories.learning.items.collaborate.answer')
                }
            ]
        }
    ];

    // Filter FAQ items based on search term and category
    const filteredFaqData = faqData.filter(category => {
        if (activeCategory !== 'all' && category.category !== activeCategory) {
            return false;
        }
        
        if (searchTerm) {
            const hasMatchingItem = category.items.some(item => 
                item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.answer.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return hasMatchingItem;
        }
        
        return true;
    });

    // Toggle FAQ item open/closed
    const toggleItem = (categoryIndex, itemIndex) => {
        const key = `${categoryIndex}-${itemIndex}`;
        const newOpenItems = new Set(openItems);
        
        if (newOpenItems.has(key)) {
            newOpenItems.delete(key);
        } else {
            newOpenItems.add(key);
        }
        
        setOpenItems(newOpenItems);
    };

    // Get category icon
    // eslint-disable-next-line no-unused-vars
    const getCategoryIcon = (category) => {
        switch (category) {
            case 'general': return FaQuestion;
            case 'features': return FaRocket;
            case 'account': return FaUser;
            case 'technical': return FaShieldAlt;
            case 'learning': return FaGraduationCap;
            default: return FaQuestion;
        }
    };

    // Get category color
    const getCategoryColor = (category) => {
        switch (category) {
            case 'general': return 'from-blue-500 to-blue-600';
            case 'features': return 'from-sky-500 to-sky-600';
            case 'account': return 'from-blue-600 to-blue-700';
            case 'technical': return 'from-slate-500 to-slate-600';
            case 'learning': return 'from-blue-500 to-sky-500';
            default: return 'from-gray-500 to-gray-600';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <Header />
            
            <div className="max-w-6xl mx-auto px-4 pt-28 sm:pt-32 pb-16">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                        <FaQuestion className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl md:text-[2rem] font-bold tracking-tight text-slate-900 dark:text-white mb-4">
                        {t('faqPage.title')}
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
                        {t('faqPage.subtitle')} 
                        <a href="/contact" className="text-blue-600 dark:text-blue-400 hover:underline ms-1">
                            {t('faqPage.contactSupport')}
                        </a>
                    </p>
                </motion.div>

                {/* Search and Filter Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="mb-8"
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                        {/* Search Bar */}
                        <div className="mb-6">
                            <div className="relative">
                                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder={t('faqPage.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full ps-12 pe-4 py-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-4 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-all duration-200 text-lg"
                                />
                            </div>
                        </div>

                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setActiveCategory('all')}
                                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                    activeCategory === 'all'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {t('faqPage.allCategories')}
                            </button>
                            {faqData.map((category, index) => (
                                <button
                                    key={category.category}
                                    onClick={() => setActiveCategory(category.category)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                                        activeCategory === category.category
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    <category.icon className="w-4 h-4" />
                                    {category.title}
                                </button>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* FAQ Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="space-y-6"
                >
                    {filteredFaqData.length > 0 ? (
                        filteredFaqData.map((category, categoryIndex) => (
                            <div key={category.category} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                                {/* Category Header */}
                                <div className={`bg-gradient-to-r ${getCategoryColor(category.category)} p-6 text-white`}>
                                    <div className="flex items-center gap-3">
                                        <category.icon className="w-6 h-6" />
                                        <h2 className="text-xl font-semibold">{category.title}</h2>
                                    </div>
                                </div>

                                {/* FAQ Items */}
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {category.items.map((item, itemIndex) => {
                                        const key = `${categoryIndex}-${itemIndex}`;
                                        const isOpen = openItems.has(key);
                                        
                                        return (
                                            <motion.div
                                                key={key}
                                                initial={false}
                                                animate={{ height: isOpen ? 'auto' : 'auto' }}
                                                className="border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                                            >
                                                <button
                                                    onClick={() => toggleItem(categoryIndex, itemIndex)}
                                                    className="w-full px-6 py-4 text-start hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-between"
                                                >
                                                    <span className="font-medium text-gray-900 dark:text-white pe-4">
                                                        {item.question}
                                                    </span>
                                                    <div className="flex-shrink-0">
                                                        {isOpen ? (
                                                            <FaChevronUp className="w-5 h-5 text-gray-500" />
                                                        ) : (
                                                            <FaChevronDown className="w-5 h-5 text-gray-500" />
                                                        )}
                                                    </div>
                                                </button>
                                                
                                                <AnimatePresence>
                                                    {isOpen && (
                                                        <motion.div
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            className="px-6 pb-4"
                                                        >
                                                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                                                {item.answer}
                                                            </p>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12">
                            <FaSearch className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {t('faqPage.noQuestionsFound')}
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400">
                                {t('faqPage.noQuestionsDescription')}
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Contact Support Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-16 bg-blue-600 rounded-2xl p-8 text-white text-center"
                >
                    <div className="max-w-2xl mx-auto">
                        <FaHeadset className="w-16 h-16 mx-auto mb-6 opacity-80" />
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">
                            {t('faqPage.stillHaveQuestions')}
                        </h2>
                        <p className="text-sm sm:text-base md:text-lg mb-6 opacity-90">
                            {t('faqPage.supportDescription')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="/contact"
                                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-2xl hover:bg-gray-100 transition-colors duration-200"
                            >
                                {t('faqPage.contactSupport')}
                            </a>
                            <a
                                href="/support"
                                className="inline-flex items-center px-6 py-3 border border-white text-white font-semibold rounded-2xl hover:bg-white hover:text-blue-600 transition-colors duration-200"
                            >
                                {t('faqPage.visitSupportCenter')}
                            </a>
                        </div>
                    </div>
                </motion.div>

                {/* Quick Tips Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-16 grid md:grid-cols-3 gap-6"
                >
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaLightbulb className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('faqPage.proTips')}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {t('faqPage.proTipsDescription')}
                        </p>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaGlobe className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('faqPage.multiLanguage')}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {t('faqPage.multiLanguageDescription')}
                        </p>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
                        <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaCrown className="w-6 h-6 text-indigo-600" />
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t('faqPage.premiumFeatures')}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {t('faqPage.premiumFeaturesDescription')}
                        </p>
                    </div>
                </motion.div>
            </div>
            
            <Footers />
        </div>
    );
};

export default FAQ;
